import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";

// Components
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import KOGSection from "@/main/components/shared/kogSection/kogSection";

// Redux & services
import { KeyValue } from "@/main/models/common";
import { CompanyFilterRequest } from "@/main/models/request";
import { getOptionsStatus } from "@/main/services/data/data";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import {
	getDefaultCountryOptionDrop,
	getDefaultOptionDrop,
	getTranslateByKey,
	isNullUndefined,
} from "@/main/services/utils";
import { getCompany, resetReducerCompany, useCompanyReducer } from "@/main/store/company";
import { getCompanyType, useCompanyTypeReducer } from "@/main/store/companyType";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { userIsAdmin } from "@/main/services/utilsUser";
import { useUserReducer } from "@/main/store/user";

// Local components
import { IFormValuesRegisterReportTemplate } from "../registerReportTemplateScreen";
import * as S from "./styles";
import { getReportTemplateType, useReportTemplateTypeReducer } from "@/main/store/reportTemplateType";

interface ISectionGeneralDataProps {
	isEdit: boolean;
	generateIdScreen: (id: string) => string;
}

function SectionGeneralData({ isEdit, generateIdScreen }: ISectionGeneralDataProps) {
	const dispatch = useDispatch();

	const { currentUser: userReducer } = useUserReducer();
	const { countriesUser: countryReducer } = useCountryReducer();
	const { companyType: companyTypeReducer } = useCompanyTypeReducer();
	const { company: companyReducer } = useCompanyReducer();
	const { reportTemplateType: reportTemplateTypeReducer } = useReportTemplateTypeReducer();

	const { control, setValue, watch } = useFormContext<IFormValuesRegisterReportTemplate>();
	const typeCompanyUser = !userIsAdmin() ? String(userReducer?.idCompanyType) : null;

	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countryReducer.data);
	const optionDefaultCompanyTypeDrop = getDefaultOptionDrop(companyTypeReducer.data, typeCompanyUser);
	const optionDefaultCompanyDrop = getDefaultOptionDrop(companyReducer.data);

	const watcherCountry = watch("country");
	const watcherCompanyType = watch("companyType");

	function callCompanyDrop(countrySelected: KeyValue | null, companyTypeSelected: KeyValue | null) {
		dispatch(resetReducerCompany());
		setValue("company", null);

		if (isNullUndefined(companyTypeSelected?.key) || isNullUndefined(countrySelected?.key)) return;
		const request: CompanyFilterRequest = {
			guidCountry: String(countrySelected.key),
			type: HeadQuarterBranchFilterEnum.All,
			idCompanyType: companyTypeSelected.key,
		};
		dispatch(getCompany(request));
	}

	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
		if (isNullUndefined(companyTypeReducer.data)) dispatch(getCompanyType());
		if (isNullUndefined(reportTemplateTypeReducer.data)) dispatch(getReportTemplateType());
	}, []);

	useEffect(() => {
		if (isEdit) return;
		if (optionDefaultCountryDrop && optionDefaultCompanyTypeDrop) {
			callCompanyDrop(optionDefaultCountryDrop, optionDefaultCompanyTypeDrop);
		}
	}, [isEdit, optionDefaultCountryDrop, optionDefaultCompanyTypeDrop]);

	const disabledCountry =
		isEdit ||
		!isNullUndefined(optionDefaultCountryDrop) ||
		isNullUndefined(countryReducer.data) ||
		countryReducer.isLoading ||
		countryReducer.data.length === 1;
	const disabledCompanyType =
		isEdit ||
		!isNullUndefined(optionDefaultCompanyTypeDrop) ||
		isNullUndefined(companyTypeReducer.data) ||
		companyTypeReducer.isLoading ||
		companyTypeReducer.data.length === 1;
	const disabledCompany =
		isEdit ||
		!isNullUndefined(optionDefaultCompanyDrop) ||
		isNullUndefined(companyReducer.data) ||
		companyReducer.isLoading ||
		companyReducer.data.length === 1;

	return (
		<KOGSection title={getTranslateByKey("dynamicReport.generalData")}>
			<S.Container>
				<KogAutoCompleteControlled
					required
					isDefaultValueUnique={!isEdit}
					valueDefaultKey={isEdit ? undefined : optionDefaultCountryDrop?.key}
					id={generateIdScreen("drop-country")}
					label={getTranslateByKey("information.country")}
					nameCustom="country"
					control={control}
					options={countryReducer.data}
					isLoading={countryReducer.isLoading}
					rulesCustom={{ required: true }}
					onChangeCustom={(countrySelected) => {
						callCompanyDrop(countrySelected, watcherCompanyType);
					}}
					setValueCustom={setValue}
					disabled={disabledCountry}
				/>
				<KogAutoCompleteControlled
					required
					isDefaultValueUnique={!isEdit}
					valueDefaultKey={isEdit ? undefined : optionDefaultCompanyTypeDrop?.key}
					id={generateIdScreen("general-drop-companyType")}
					label={getTranslateByKey("information.companyType")}
					nameCustom="companyType"
					control={control}
					options={companyTypeReducer.data}
					isLoading={companyTypeReducer.isLoading}
					rulesCustom={{ required: true }}
					onChangeCustom={(companyTypeSelected) => {
						callCompanyDrop(watcherCountry, companyTypeSelected);
					}}
					setValueCustom={setValue}
					disabled={disabledCompanyType}
				/>
				<KogAutoCompleteControlled
					required
					isDefaultValueUnique={!isEdit}
					valueDefaultKey={isEdit ? undefined : optionDefaultCompanyDrop?.key}
					id={generateIdScreen("drop-company")}
					label={getTranslateByKey("information.company")}
					nameCustom="company"
					control={control}
					options={companyReducer.data}
					isLoading={companyReducer.isLoading}
					rulesCustom={{ required: true }}
					onChangeCustom={() => {}}
					setValueCustom={setValue}
					disabled={disabledCompany}
				/>

				<KogInputTextControlledNew
					id={generateIdScreen("txt-reportName")}
					label={getTranslateByKey("information.reportTemplateName")}
					name="reportName"
					required
					control={control}
				/>

				<KogAutoCompleteControlled
					required
					id={generateIdScreen("drop-report-template-type")}
					label={getTranslateByKey("information.type")}
					nameCustom="type"
					control={control}
					options={reportTemplateTypeReducer.data}
					isLoading={reportTemplateTypeReducer.isLoading}
					rulesCustom={{ required: true }}
					onChangeCustom={() => {}}
					setValueCustom={setValue}
					disabled={false}
				/>

				<KogAutoCompleteControlled
					required
					id={generateIdScreen("drop-status")}
					label={getTranslateByKey("information.status")}
					nameCustom="status"
					control={control}
					options={getOptionsStatus()}
					isLoading={false}
					rulesCustom={{ required: true }}
					onChangeCustom={() => {}}
					setValueCustom={setValue}
					disabled={false}
				/>
			</S.Container>
		</KOGSection>
	);
}

export default SectionGeneralData;
